import {
  format,
  isValid,
  isSameDay,
  setHours,
  setMinutes,
  setSeconds,
  getDay,
  subMilliseconds,
  subSeconds,
  subMinutes,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";

/**
 * Wrapper for Datetime Utility
 *  - based off of datetime wrapper in support-api
 */
class DateTime {
  dateTime: Date;

  value: Date;

  constructor(dateTime?: string | number | Date) {
    this.dateTime = dateTime ? new Date(dateTime) : new Date();

    if (!isValid(this.dateTime)) {
      throw new Error(
        "[DateTime] The given value could not be successfully converted to a valid Date object"
      );
    }

    this.value = this.dateTime;
  }

  format(formatString: string): string {
    try {
      const formattedDate = format(this.value, formatString);
      return formattedDate;
    } catch (err) {
      return this.value.toLocaleString();
    }
  }

  subtract(amount: number, unit: string): DateTime {
    switch (unit) {
      // default: milliseconds
      default:
        this.value = subMilliseconds(this.dateTime, amount);
        return this;
      case "second":
        this.value = subSeconds(this.dateTime, amount);
        return this;
      case "minute":
        this.value = subMinutes(this.dateTime, amount);
        return this;
      case "hour":
        this.value = subHours(this.dateTime, amount);
        return this;
      case "day":
        this.value = subDays(this.dateTime, amount);
        return this;
      case "week":
        this.value = subWeeks(this.dateTime, amount);
        return this;
      case "month":
        this.value = subMonths(this.dateTime, amount);
        return this;
      case "year":
        this.value = subYears(this.dateTime, amount);
        return this;
    }
  }

  setHours(hours: number): DateTime {
    this.value = setHours(this.value, hours);
    return this;
  }

  setMinutes(minutes: number): DateTime {
    this.value = setMinutes(this.value, minutes);
    return this;
  }

  setSeconds(seconds: number): DateTime {
    this.value = setSeconds(this.value, seconds);
    return this;
  }

  getDay(): number {
    return getDay(this.value);
  }

  toLocal(): Date {
    return this.value;
  }

  /**
   * returns true if date matches current date.
   * @param compareTo {Date}
   * @returns boolean
   */
  isSameDay(compareTo: Date): boolean {
    return isSameDay(this.value, compareTo);
  }

  /**
   * Get relative time from current date
   */
  getRelativeTimeFromToday = (): string => {
    const relativeDays = {
      0: "Yesterday",
      7: "This week",
      14: "More than a week ago",
      default: "More than two weeks ago",
    };

    // From date should be from time = 00:00:00
    const dateFrom = new Date();
    dateFrom.setHours(0);
    dateFrom.setMinutes(0);
    dateFrom.setSeconds(0);
    dateFrom.setMilliseconds(0);

    const daysPastFromDatetime = Math.floor(
      (dateFrom.getTime() - this.dateTime.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysPastFromDatetime > 14) {
      return relativeDays.default;
    }
    if (daysPastFromDatetime <= 14 && daysPastFromDatetime > 7) {
      return relativeDays[14];
    }
    if (daysPastFromDatetime <= 7 && daysPastFromDatetime >= 2) {
      return relativeDays[7];
    }

    if (daysPastFromDatetime < 0) {
      return "Today";
    }
    return relativeDays[0];
  };

  static determineRemainingMinutes = (
    currentDateTime: DateTime,
    compareDateTime: DateTime,
    delayMinutes: number
  ): number => {
    const delayDateTime: DateTime = currentDateTime.subtract(
      delayMinutes,
      "minute"
    );
    const remainingMinutesFloat =
      (compareDateTime.dateTime.getTime() - delayDateTime.value.getTime()) /
      60000;
    return Math.ceil(remainingMinutesFloat);
  };
}

export default DateTime;
